import React, { useState, useEffect, useCallback } from "react";
import { Loader } from "../../../components/loader/index.js";

export const Participants = ({ title }) => {
  const [users, setUsers] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Memoized fetch functions
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/user-scores/course/${title}`
      );
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.data);
      } else {
        setFetchError("Failed to fetch user scores");
      }
    } catch (error) {
      setFetchError("Error fetching data: " + error.message);
    }
  }, [title]);

  const fetchUserDetails = useCallback(async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/user-scores/course/${title}/user/${userId}`
      );
      const data = await response.json();
      if (data.success) {
        setUserDetails(prev => ({ ...prev, [userId]: data }));
        setExpandedUserId(userId);
      } else {
        setFetchError("Failed to fetch user details");
      }
    } catch (error) {
      setFetchError("Error fetching user details: " + error.message);
    } finally {
      setDetailsLoading(false);
    }
  }, [title, expandedUserId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const UserDetailsPanel = ({ userId }) => {
    const details = userDetails[userId];
    
    if (detailsLoading) {
      return (
        <div className="flex justify-center items-center h-24">
          <Loader w="6" h="6" color="orange" />
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-xs text-gray-500">
          No details available
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Quiz score sheet
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Topic</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Correct</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Time</th>
                  <th className="py-2 px-3 text-left text-xs font-semibold text-gray-900">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {details.module_scores.data.map((module) => (
                  <tr key={module.module_id}>
                    <td className="py-2 px-3 text-xs text-gray-900">{module?.module_name}</td>
                    <td className="py-2 px-3 text-xs text-gray-500">{module?.correct_answers}</td>
                    <td className="py-2 px-3 text-xs text-gray-500">{module?.total_time_taken}s</td>
                    <td className="py-2 px-3 text-xs text-gray-500">{module?.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">
            Answers
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-gray-900 w-[15%]">Topic</th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-gray-900 w-[30%]">Question</th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-gray-900 w-[30%]">Answer</th>
                  <th className="py-2 px-2 text-center text-xs font-semibold text-gray-900 w-[5%]">✓/✗</th>
                  <th className="py-2 px-2 text-center text-xs font-semibold text-gray-900 w-[5%]">Time</th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-gray-900 w-[15%]">Justification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {details.module_answers.map((answer) => (
                  <tr key={answer.id} className="hover:bg-gray-50">
                    <td className="py-2 px-2 text-xs text-gray-900">
                      <div className="line-clamp-2">{answer.module_name}</div>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500">
                      <div className="line-clamp-2 hover:line-clamp-none cursor-help" 
                           title={answer.question}>
                        {answer.question}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500">
                      <div className="line-clamp-2 hover:line-clamp-none cursor-help" 
                           title={answer.answer}>
                        {answer.answer}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
                        answer.is_correct ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}>
                        {answer.is_correct ? "✓" : "✗"}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500 text-center">
                      {answer.time_taken_seconds}s
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500">
                      {answer.justification ? (
                        <div className="line-clamp-2 hover:line-clamp-none cursor-help" 
                             title={answer.justification}>
                          {answer.justification}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-2 sm:px-4">
      {fetchError && (
        <div className="mt-2 text-red-600 text-sm">{fetchError}</div>
      )}
      
      <div className="mt-4 flex flex-col">
        <div className="-my-2 -mx-2 sm:-mx-4">
          <div className="inline-block min-w-full py-1 align-middle">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-md">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 pl-3 pr-2 text-left text-sm font-semibold text-gray-900">Username</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Email</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {users.map((user) => (
                    <React.Fragment key={user.id}>
                      <tr 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => fetchUserDetails(user.id)}
                      >
                        <td className="whitespace-nowrap py-3 pl-3 pr-2 text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {user.score}
                        </td>
                      </tr>
                      
                      {expandedUserId === user.id && (
                        <tr>
                          <td colSpan="3" className="bg-gray-50 p-0">
                            <div className="p-3">
                              <UserDetailsPanel userId={user.id} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};